"use client";

/**
 * The site's menu, always in reach: logo, Shop, search and bag, fixed to the
 * top of the screen. Over the first screen it is clear (the page looks as
 * designed); once you scroll it becomes frosted glass that takes the tone of
 * the world under it (dark glass over dark worlds, light glass over light),
 * and the world chips slide in beneath it with the one you are in lit — so
 * any world, the shop and the bag are always one tap away.
 */

import { useEffect, useRef, useState, type CSSProperties } from "react";
import BagButton from "./BagButton";
import { Icon } from "./icons";
import s from "./colour-worlds.module.css";

export interface BarChip {
  key: string;
  label: string;
  href: string;
  dot: string;
  live?: boolean;
}

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(" ");

/** Worlds without a chip of their own light their neighbour's. */
const CHIP_OF: Record<string, string> = { premium: "runner", charge: "power" };

/** Mutoni's "M." mark, traced from the brand art, in the bar's own colour. */
function Mark() {
  return (
    <svg viewBox="676 704 696 640.1" width={32} height={30} aria-hidden="true" focusable="false">
      <path d="M676 704H825V731.8A199 199 0 0 0 1223 731.8V704H1372V1337H1223V1016.9A350.7 350.7 0 0 1 825 1016.9V1337H676Z" fill="currentColor" />
      <circle cx="1023.6" cy="1269.6" r="74.5" fill="currentColor" />
    </svg>
  );
}

export default function StickyBar({ brandName, chips }: { brandName: string; chips: BarChip[] }) {
  const [scrolled, setScrolled] = useState(false);
  const [showChips, setShowChips] = useState(false);
  const [active, setActive] = useState("sound");
  const [light, setLight] = useState(false);
  const strip = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    // The bar's chips come in once the first screen's own chips have gone up.
    const heroChips = document.querySelector("[data-hero-chips]");
    const chipsWatch = new IntersectionObserver(([e]) => setShowChips(!e.isIntersecting && e.boundingClientRect.top < 0), {
      rootMargin: "-64px 0px 0px 0px",
    });
    if (heroChips) chipsWatch.observe(heroChips);

    // The world under the bar: its chip is lit and the glass takes its tone.
    const worldWatch = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const el = e.target as HTMLElement;
          setActive(el.dataset.world ?? "");
          setLight(el.dataset.tone === "light");
        }
      },
      { rootMargin: "-72px 0px -84% 0px" }
    );
    document.querySelectorAll("[data-world]").forEach((el) => worldWatch.observe(el));

    return () => {
      window.removeEventListener("scroll", onScroll);
      chipsWatch.disconnect();
      worldWatch.disconnect();
    };
  }, []);

  const lit = CHIP_OF[active] ?? active;

  // Keep the lit chip in view on a narrow screen.
  useEffect(() => {
    const row = strip.current;
    const chip = row?.querySelector<HTMLElement>(`[data-chip="${lit}"]`);
    if (!row || !chip || !showChips) return;
    row.scrollTo({ left: chip.offsetLeft - (row.clientWidth - chip.offsetWidth) / 2, behavior: "smooth" });
  }, [lit, showChips]);

  return (
    <header className={cx(s.bar, scrolled && (light ? s.barLight : s.barGlass))}>
      <div className={s.barRow}>
        <a href="/" aria-label={`${brandName} home`} className={s.logo}>
          <Mark />
        </a>
        <div className={s.tools}>
          <a href="/shop" className={s.shopPill}>
            Shop
          </a>
          <a href="/shop" aria-label="Search the shop" className={s.glassBtn}>
            <Icon name="search" size={19} />
          </a>
          <BagButton className={s.glassBtn} badgeClassName={s.badge} />
        </div>
      </div>
      <nav ref={strip} className={cx(s.barChips, showChips && s.barChipsOn)} aria-label="Worlds" aria-hidden={!showChips} inert={!showChips}>
        {chips.map((c) => (
          <a
            key={c.key}
            href={c.href}
            data-chip={c.key}
            className={cx(s.chip, c.live && s.live, c.key === lit && s.chipOn)}
            style={{ "--dot": c.dot } as CSSProperties}
            aria-current={c.key === lit ? "true" : undefined}
          >
            <i />
            {c.label}
          </a>
        ))}
      </nav>
    </header>
  );
}
